import React, { useState } from 'react';
import { AppProvider, useApp } from './context/AppContext';

import LoginPage from './components/pages/Auth/LoginPage';
import Sidebar from './components/layout/Sidebar';
import Topbar from './components/layout/Topbar';
import { Toast, Modal, FormGroup, Textarea, InfoBanner, Btn } from './components/common/UI';

import Dashboard from './components/pages/Dashboard/Dashboard';
import Approvals from './components/pages/Approvals/Approvals';
import AdminSetup from './components/pages/Admin/AdminSetup';
import AdminUsers from './components/pages/Admin/AdminUsers';
import NewEntry from './components/pages/Warehouse/NewEntry';
import StockList from './components/pages/Warehouse/StockList';
import DeliveryOrders from './components/pages/Delivery/DeliveryOrders';
import { MyStock, MyDeliveries } from './components/pages/Customer/CustomerPages';
import ProfilePage from './components/pages/Profile/ProfilePage';

/* ───── Route → Page map ───── */
function Router({ page, pageData, onNav, loggedUser }) {
  const props = { onNav };
  switch (page) {
    case 'dashboard': 
      if (loggedUser?.role === 'customer') return <MyStock onNav={onNav} loggedUser={loggedUser} />;
      return <Dashboard      {...props} />;
    case 'approvals': return <Approvals       {...props} />;
    case 'admin-customers': return <AdminSetup section="customers" {...props} />;
    case 'admin-vessels': return <AdminSetup section="vessels" {...props} />;
    case 'admin-suppliers': return <AdminSetup section="suppliers" {...props} />;
    case 'admin-masters': return <AdminSetup section="masters" {...props} />;
    case 'admin-users': return <AdminUsers {...props} />;
    case 'new-entry': return <NewEntry        {...props} draftData={pageData || null} />;
    case 'stock-list': return <StockList       {...props} />;
    case 'delivery': return <DeliveryOrders  {...props} />;
    case 'my-stock': return <MyStock         {...props} loggedUser={loggedUser} />;
    case 'my-deliveries': return <MyDeliveries    {...props} loggedUser={loggedUser} />;
    case 'profile': return <ProfilePage     {...props} loggedUser={loggedUser} />;
    default: return <Dashboard       {...props} />;
  }
}

/* ───── Global reject modal (used from Dashboard quick-reject) ───── */
function GlobalRejectModal() {
  const { modal, closeModal, rejectStock, showToast } = useApp();
  const [reason, setReason] = React.useState('');

  React.useEffect(() => { if (!modal.open) setReason(''); }, [modal.open]);

  if (!modal.open || modal.type !== 'reject') return null;

  const handleConfirm = () => {
    if (!reason.trim()) { showToast('Please provide a rejection reason', 'error'); return; }
    rejectStock(modal.data?.id, reason);
    closeModal();
  };

  return (
    <Modal open onClose={closeModal}
      title="Reject Entry"
      subtitle={modal.data ? `${modal.data.type} — ${modal.data.po}` : ''}
      icon="✕" iconBg="var(--danger-bg)"
      footer={<>
        <Btn variant="outline" onClick={closeModal}>Cancel</Btn>
        <Btn variant="danger" onClick={handleConfirm}>✕ Reject &amp; Notify</Btn>
      </>}>
      <InfoBanner variant="warn">
        ⚠ The warehouse staff will see this reason and must correct before resubmitting.
      </InfoBanner>
      <FormGroup label="Rejection Reason" required>
        <Textarea value={reason} onChange={e => setReason(e.target.value)}
          placeholder="e.g. Missing photo evidence. Upload at least 2 clear photos..."
          style={{ minHeight: 100 }} />
      </FormGroup>
    </Modal>
  );
}

/* ───── Shell ───── */
function Shell() {
  const { role, setRole, toast, loggedUser: ctxUser, fetchAllData, updateUser, currentPage: page, navigate: handleNav, pageData } = useApp();

  // Persistence logic: Check if user exists in context/localstorage
  const [loggedIn, setLoggedIn] = useState(!!ctxUser?.token);
  const [loggedUser, setLoggedUserInternal] = useState(ctxUser);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // handleNav removed as it comes from context now

  const handleLogin = (userRole, userInfo) => {
    setRole(userRole);
    setLoggedUserInternal(userInfo);
    setLoggedIn(true);
    // Sync context so Topbar/Sidebar show real logged-in user name
    updateUser({ name: userInfo.name, email: userInfo.email, bio: `${userInfo.role} account`, phone: '' }, true);
    fetchAllData();
  };

  const handleLogout = () => {
    localStorage.removeItem('userInfo');
    localStorage.removeItem('currPage');
    setLoggedIn(false);
    setLoggedUserInternal(null);
    setRole('admin'); // Reset default
    handleNav('dashboard');
  };

  /* Role defaults (Only if no saved page) */
  React.useEffect(() => {
    if (!loggedIn || localStorage.getItem('currPage')) return;
    const defaults = { admin: 'dashboard', warehouse: 'dashboard', customer: 'my-stock' };
    if (page === 'dashboard' && role === 'customer') {
      handleNav('my-stock');
    } else {
      handleNav(defaults[role] || 'dashboard');
    }
  }, [role, loggedIn, handleNav]);

  if (!loggedIn) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <div style={{ display: 'flex', height: '100vh', width: '100vw', overflow: 'hidden' }}>
      <Sidebar current={page} onNav={handleNav} onLogout={handleLogout} loggedUser={loggedUser}
        isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Topbar current={page} onNav={handleNav} onLogout={handleLogout} loggedUser={loggedUser}
          onMenuToggle={() => setSidebarOpen(s => !s)} />
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', touchAction: 'pan-y', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <Router page={page} pageData={pageData} onNav={handleNav} loggedUser={loggedUser} />
        </div>
      </div>
      <Toast show={toast.show} msg={toast.msg} type={toast.type} />
      <GlobalRejectModal />
    </div>
  );
}

/* ───── Root ───── */
export default function App() {
  return (
    <AppProvider>
      <Shell />
    </AppProvider>
  );
}
